import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Flame, Handshake, Globe, Instagram, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { Pill, Loader, Empty } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const CATS = ["all", "startup", "local_business", "creator", "community_project"];
const catLabel = (c: string) => c.replace(/_/g, " ");
const field = "w-full h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors";

const emptyForm = { name: "", category: "startup", city: "", tagline: "", about: "", cover_image: "", website: "", instagram: "", looking_for: "" };

const Spotlight = () => {
  const { user, login } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("recent");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ ...emptyForm });
  const [connectTo, setConnectTo] = useState<any>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [endorsing, setEndorsing] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get("/spotlight", { params: { category: cat, sort, q: q || undefined } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [cat, sort, q]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/spotlight", { ...f, cover_image: f.cover_image || undefined, website: f.website || undefined, instagram: f.instagram || undefined, looking_for: f.looking_for || undefined });
      toast.success("Listing submitted for review.");
      setF({ ...emptyForm });
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const endorse = async (id: string) => {
    if (!user) return login();
    try {
      const { data } = await api.post(`/spotlight/${id}/endorse`);
      setItems(items.map((s) => (s.spotlight_id === id ? { ...s, endorsements: data.endorsements } : s)));
      toast.success("Endorsed.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const sendConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/spotlight/${connectTo.spotlight_id}/connect`, { message: msg });
      toast.success(`Request sent to ${connectTo.name}. You'll get an email if they accept.`);
      setConnectTo(null);
      setMsg("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16" data-testid="spotlight-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">Back the builders</span>
          <h1 className="mt-3 font-bebas text-5xl sm:text-6xl tracking-wide uppercase">Spotlight / Support</h1>
          <p className="mt-3 text-sm text-white/45 max-w-xl">Startups, local businesses, creators and community projects from the circle. Endorse the ones you believe in, or send a connect request to collaborate.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border border-white/10 px-3 h-11 bg-white/[0.03] focus-within:border-neon/60 transition-colors">
            <Search size={15} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search listings" data-testid="spotlight-search" className="bg-transparent outline-none text-sm w-48 placeholder:text-white/30" />
          </div>
          {user ? (
            <button onClick={() => setOpen(!open)} data-testid="spotlight-submit-toggle" className="bg-neon px-6 h-11 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow duration-200">
              <Plus size={15} /> Get spotlighted
            </button>
          ) : (
            <button onClick={login} data-testid="spotlight-login-btn" className="border border-white/20 px-6 h-11 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
              Sign in to submit
            </button>
          )}
        </div>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-10 border border-white/10 bg-ink-surface p-7 grid gap-4 md:grid-cols-2" data-testid="spotlight-form">
          <input required placeholder="Name of the venture / project" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="spotlight-name-input" className={field} />
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="spotlight-category-select" className={field}>
            {CATS.filter((c) => c !== "all").map((c) => <option key={c} value={c} className="bg-ink-surface">{catLabel(c)}</option>)}
          </select>
          <input required placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="spotlight-city-input" className={field} />
          <input required placeholder="One-line tagline" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} data-testid="spotlight-tagline-input" className={field} />
          <input placeholder="Website (optional)" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="spotlight-website-input" className={field} />
          <input placeholder="Instagram handle (optional)" value={f.instagram} onChange={(e) => setF({ ...f, instagram: e.target.value })} className={field} />
          <input placeholder="What support are you looking for? (optional)" value={f.looking_for} onChange={(e) => setF({ ...f, looking_for: e.target.value })} data-testid="spotlight-lookingfor-input" className={field} />
          <textarea required rows={4} placeholder="Tell the circle what you're building and why it matters…" value={f.about} onChange={(e) => setF({ ...f, about: e.target.value })} data-testid="spotlight-about-input" className="md:col-span-2 w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors" />
          <div className="md:col-span-2">
            <ImageUpload value={f.cover_image} onChange={(url) => setF({ ...f, cover_image: url })} label="Cover photo (optional)" testid="spotlight-cover-upload" />
          </div>
          <button data-testid="spotlight-submit-btn" className="md:col-span-2 h-11 bg-neon text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Submit for review</button>
        </form>
      )}

      <div className="mt-10 flex flex-wrap items-center gap-2">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`spotlight-cat-${c}`}
            className={`px-4 h-9 text-[11px] font-bold uppercase tracking-[0.18em] border transition-colors duration-200 ${
              cat === c ? "border-neon text-neon bg-neon/10" : "border-white/12 text-white/50 hover:text-white"
            }`}>
            {catLabel(c)}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {["recent", "top"].map((s) => (
            <button key={s} onClick={() => setSort(s)} data-testid={`spotlight-sort-${s}`}
              className={`px-4 h-9 text-[11px] font-bold uppercase tracking-[0.18em] border transition-colors duration-200 ${
                sort === s ? "border-pcblue text-pcblue" : "border-white/12 text-white/40 hover:text-white"
              }`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <div className="mt-12"><Empty text="No listings yet. Be the first to get spotlighted." testid="spotlight-empty" /></div>
      ) : (
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {items.map((s, i) => (
            <div key={s.spotlight_id} className="border border-white/10 bg-ink-surface flex flex-col group hover:border-neon/40 transition-colors" data-testid={`spotlight-card-${i}`}>
              {s.cover_image && (
                <div className="h-40 overflow-hidden">
                  <img src={s.cover_image} alt={s.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                </div>
              )}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="blue">{catLabel(s.category)}</Pill>
                  <Pill>{s.city}</Pill>
                </div>
                <h3 className="mt-4 font-outfit text-xl font-bold group-hover:text-neon transition-colors">{s.name}</h3>
                <p className="mt-1 text-sm text-white/60">{s.tagline}</p>
                <p className="mt-3 text-sm text-white/45 line-clamp-3">{s.about}</p>
                {s.looking_for && (
                  <p className="mt-3 text-[11px] uppercase tracking-[0.16em] text-pcyellow">Looking for: <span className="text-white/70 normal-case tracking-normal">{s.looking_for}</span></p>
                )}
                <div className="mt-4 flex items-center gap-4 text-white/35">
                  {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="hover:text-neon transition-colors" data-testid={`spotlight-website-${i}`}><Globe size={15} /></a>}
                  {s.instagram && <a href={`https://instagram.com/${s.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="hover:text-neon transition-colors"><Instagram size={15} /></a>}
                  <span className="ml-auto text-[11px] uppercase tracking-[0.16em]">by {s.owner_name}</span>
                </div>
                <div className="mt-5 pt-5 border-t border-white/8 flex gap-3">
                  <button onClick={() => endorse(s.spotlight_id)} disabled={endorsing === s.spotlight_id} data-testid={`spotlight-endorse-${i}`}
                    className="flex-1 h-10 border border-white/15 flex items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] hover:border-neon hover:text-neon transition-colors disabled:opacity-50">
                    <Flame size={14} /> Endorse · {s.endorsements}
                  </button>
                  <button onClick={() => (user ? setConnectTo(s) : login())} data-testid={`spotlight-connect-${i}`}
                    className="flex-1 h-10 bg-neon flex items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] hover:pc-glow transition-shadow">
                    <Handshake size={14} /> Connect
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {connectTo && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm grid place-items-center px-5" data-testid="connect-modal">
          <form onSubmit={sendConnect} className="w-full max-w-lg border border-white/15 bg-ink-surface p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">Connect request</span>
                <h3 className="mt-2 font-bebas text-3xl tracking-wide uppercase">{connectTo.name}</h3>
              </div>
              <button type="button" onClick={() => setConnectTo(null)} data-testid="connect-modal-close" className="text-white/40 hover:text-neon transition-colors"><X /></button>
            </div>
            <p className="mt-3 text-sm text-white/50">Tell them why you want to connect or collaborate. Your email is shared only if they accept.</p>
            <textarea required minLength={10} rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Hi! I'd love to collaborate on…" data-testid="connect-message-input"
              className="mt-5 w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors" />
            <button disabled={busy} data-testid="connect-send-btn" className="mt-4 h-11 w-full bg-neon text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow disabled:opacity-50">
              {busy ? "Sending…" : "Send request"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Spotlight;
